import React, { useState } from 'react'
import {
  Alert,
  Badge,
  Button,
  Card,
  Col,
  Descriptions,
  Divider,
  Input,
  Row,
  Space,
  Statistic,
  Tag,
  Typography,
  Upload,
  message,
} from 'antd'
import {
  CheckCircleFilled,
  CheckCircleOutlined,
  CloseCircleOutlined,
  CloudUploadOutlined,
  DeleteOutlined,
  FileOutlined,
  FilePdfOutlined,
  IdcardOutlined,
  PictureOutlined,
  QuestionCircleOutlined,
  ScanOutlined,
  UserOutlined,
  WarningOutlined,
} from '@ant-design/icons'
import type { UploadFile } from 'antd'
import { ocrAPI, OcrResult } from '../services/api'

const { Title, Text } = Typography

// ── Utilities ─────────────────────────────────────────────────────────────────

const toBase64 = (file: File): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => resolve((reader.result as string).split(',')[1])
    reader.onerror = reject
    reader.readAsDataURL(file)
  })

const isHeic = (file: File) =>
  file.type === 'image/heic' ||
  file.type === 'image/heif' ||
  /\.(heic|heif)$/i.test(file.name)

/**
 * Convert a file to base64. HEIC/HEIF files are converted to JPEG via Canvas
 * before encoding (supported on Safari and Chrome macOS 14+).
 */
async function fileToBase64WithHeic(file: File): Promise<{ base64: string; contentType: string }> {
  if (isHeic(file)) {
    try {
      const bitmap = await createImageBitmap(file)
      const canvas = document.createElement('canvas')
      canvas.width = bitmap.width
      canvas.height = bitmap.height
      canvas.getContext('2d')!.drawImage(bitmap, 0, 0)
      const blob = await new Promise<Blob>((res, rej) =>
        canvas.toBlob(b => (b ? res(b) : rej(new Error('Canvas toBlob failed'))), 'image/jpeg', 0.92)
      )
      const base64 = await toBase64(new File([blob], file.name.replace(/\.heic?$/i, '.jpg'), { type: 'image/jpeg' }))
      return { base64, contentType: 'image/jpeg' }
    } catch {
      // Browser can't decode HEIC — send as-is and let backend try
    }
  }
  const base64 = await toBase64(file)
  return { base64, contentType: file.type || 'image/jpeg' }
}

const formatMoney = (v: number | null | undefined) =>
  v != null ? `$${v.toLocaleString('es-CO')}` : '—'

const fileIcon = (file: File) => {
  const type = file.type || ''
  if (type.includes('pdf')) return <FilePdfOutlined style={{ color: '#ff4d4f', fontSize: 18 }} />
  if (type.includes('image')) return <PictureOutlined style={{ color: '#1890ff', fontSize: 18 }} />
  return <FileOutlined style={{ color: '#8c8c8c', fontSize: 18 }} />
}

// ── Sub-components ────────────────────────────────────────────────────────────

const TriBool: React.FC<{ value: boolean | null; trueLabel?: string; falseLabel?: string }> = ({
  value, trueLabel = 'Sí', falseLabel = 'No',
}) => {
  if (value == null) return <Tag icon={<QuestionCircleOutlined />} color="default">Sin datos</Tag>
  return value
    ? <Tag icon={<CheckCircleOutlined />} color="success">{trueLabel}</Tag>
    : <Tag icon={<CloseCircleOutlined />} color="error">{falseLabel}</Tag>
}

const MethodTag: React.FC<{ method: string }> = ({ method }) => {
  const cfg: Record<string, { color: string; label: string }> = {
    regex:  { color: 'blue',    label: 'Regex PDF' },
    vision: { color: 'purple',  label: 'Vision AI' },
    llm:    { color: 'orange',  label: 'LLM texto' },
  }
  const c = cfg[method] ?? { color: 'default', label: method }
  return <Tag color={c.color} style={{ fontSize: 11 }}>{c.label}</Tag>
}

// Single file upload slot
interface SlotProps {
  label: string
  hint: string
  required?: boolean
  accept?: string
  file: File | null
  onChange: (f: File | null) => void
}
const FileSlot: React.FC<SlotProps> = ({ label, hint, required, accept = '.pdf,.jpg,.jpeg,.png,.heic,.heif', file, onChange }) => (
  <div>
    <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
      <Text strong style={{ fontSize: 13 }}>{label}</Text>
      {required && <Text type="danger" style={{ fontSize: 11 }}>requerido</Text>}
    </div>
    {file ? (
      <div style={{
        display: 'flex', alignItems: 'center', gap: 8,
        background: '#f6ffed', border: '1px solid #b7eb8f',
        borderRadius: 6, padding: '6px 10px',
      }}>
        {fileIcon(file)}
        <div style={{ flex: 1, minWidth: 0 }}>
          <Text style={{ fontSize: 12, display: 'block' }} ellipsis>{file.name}</Text>
          <Text type="secondary" style={{ fontSize: 11 }}>
            {(file.size / 1024).toFixed(0)} KB
          </Text>
        </div>
        <CheckCircleFilled style={{ color: '#52c41a', fontSize: 14 }} />
        <Button
          type="text" size="small" danger
          icon={<DeleteOutlined />}
          onClick={() => onChange(null)}
          style={{ padding: '0 4px' }}
        />
      </div>
    ) : (
      <Upload
        accept={accept} maxCount={1}
        beforeUpload={f => { onChange(f); return false }}
        showUploadList={false}
      >
        <div style={{
          border: '1px dashed #d9d9d9', borderRadius: 6,
          padding: '8px 12px', cursor: 'pointer', background: '#fafafa',
          display: 'flex', alignItems: 'center', gap: 8,
          transition: 'border-color 0.2s',
        }}
          onMouseEnter={e => (e.currentTarget.style.borderColor = '#1890ff')}
          onMouseLeave={e => (e.currentTarget.style.borderColor = '#d9d9d9')}
        >
          <CloudUploadOutlined style={{ color: '#8c8c8c', fontSize: 16 }} />
          <div>
            <Text style={{ fontSize: 12, color: '#595959' }}>Seleccionar archivo</Text>
            <br />
            <Text type="secondary" style={{ fontSize: 11 }}>{hint}</Text>
          </div>
        </div>
      </Upload>
    )}
  </div>
)

// ── Main page ─────────────────────────────────────────────────────────────────

type SlotKey = 'cc_frente' | 'cc_reverso' | 'voucher'

const OcrAnalysis: React.FC = () => {
  const [files, setFiles] = useState<Record<SlotKey, File | null>>({ cc_frente: null, cc_reverso: null, voucher: null })
  const [ccNumber, setCcNumber] = useState('')
  const [simulationId, setSimulationId] = useState('')
  const [loading, setLoading] = useState(false)
  const [result, setResult] = useState<OcrResult | null>(null)

  const setFile = (slot: SlotKey, file: File | null) => {
    setFiles(prev => ({ ...prev, [slot]: file }))
    setResult(null)
  }

  const filesCount = Object.values(files).filter(Boolean).length
  const canSubmit = !loading && (!!files.cc_frente || !!files.voucher)

  const handleSubmit = async () => {
    setLoading(true)
    try {
      const documents = []
      if (files.cc_frente) {
        const { base64, contentType } = await fileToBase64WithHeic(files.cc_frente)
        documents.push({ filename: files.cc_frente.name, base64, content_type: contentType })
      }
      if (files.cc_reverso) {
        const name = files.cc_reverso.name.toUpperCase().includes('REVERSO') ? files.cc_reverso.name : `CC REVERSO.${files.cc_reverso.name.split('.').pop()}`
        const { base64, contentType } = await fileToBase64WithHeic(files.cc_reverso)
        documents.push({ filename: name, base64, content_type: contentType })
      }
      if (files.voucher) {
        const { base64, contentType } = await fileToBase64WithHeic(files.voucher)
        documents.push({ filename: files.voucher.name, base64, content_type: contentType })
      }

      const data = await ocrAPI.analyze({ cc_number: ccNumber.trim() || undefined, simulation_id: simulationId.trim() || undefined, documents })
      setResult(data)
      data.errors.length === 0 ? message.success('Documentos procesados correctamente') : message.warning(`Procesado con ${data.errors.length} advertencia(s)`)
    } catch (err: any) {
      message.error(`Error: ${err?.response?.data?.detail || err?.message || 'Error desconocido'}`)
    } finally {
      setLoading(false)
    }
  }

  // ── Derived validation summary ──────────────────────────────────────────────
  const ccOk = result?.cross_validation.cc_match
  const nameOk = result?.cross_validation.name_match
  const bothOk = ccOk === true && nameOk === true
  const anyFail = ccOk === false || nameOk === false

  return (
    <div style={{ maxWidth: 1100, margin: '0 auto' }}>

      {/* ── Header ── */}
      <div style={{ marginBottom: 20 }}>
        <Title level={3} style={{ margin: 0 }}>Análisis OCR de Documentos</Title>
        <Text type="secondary">
          Extrae identidad e ingresos de cédulas y comprobantes de pensión / nómina docente
        </Text>
      </div>

      <Row gutter={[20, 20]} align="top">

        {/* ── Left: upload panel ── */}
        <Col xs={24} lg={9}>
          <Card
            bordered
            styles={{ body: { padding: 20 } }}
            style={{ position: 'sticky', top: 16 }}
          >
            {/* Section: Cédula */}
            <div style={{ marginBottom: 16 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
                <IdcardOutlined style={{ color: '#1890ff', fontSize: 15 }} />
                <Text strong style={{ fontSize: 13, color: '#262626' }}>Cédula de Ciudadanía</Text>
              </div>
              <Space direction="vertical" style={{ width: '100%' }} size={10}>
                <FileSlot label="Frente" hint="Cara con foto y nombre" required file={files.cc_frente} onChange={f => setFile('cc_frente', f)} />
                <FileSlot label="Reverso" hint="Cara con fecha de nacimiento" file={files.cc_reverso} onChange={f => setFile('cc_reverso', f)} />
              </Space>
            </div>

            <Divider style={{ margin: '14px 0' }} />

            {/* Section: Voucher */}
            <div style={{ marginBottom: 16 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
                <ScanOutlined style={{ color: '#722ed1', fontSize: 15 }} />
                <Text strong style={{ fontSize: 13, color: '#262626' }}>Comprobante de Ingresos</Text>
              </div>
              <FileSlot label="Volante / certificación" hint="PDF o imagen de pensión / nómina" required file={files.voucher} onChange={f => setFile('voucher', f)} />
            </div>

            <Divider style={{ margin: '14px 0' }} />

            {/* Optional fields */}
            <Space direction="vertical" style={{ width: '100%' }} size={10}>
              <div>
                <Text style={{ fontSize: 12, display: 'block', marginBottom: 4 }} strong>
                  Número de cédula <Text type="secondary">(opcional — mejora validación cruzada)</Text>
                </Text>
                <Input size="small" placeholder="Ej: 12597334" value={ccNumber} onChange={e => setCcNumber(e.target.value)} />
              </div>
              <div>
                <Text style={{ fontSize: 12, display: 'block', marginBottom: 4 }} strong>
                  Simulation ID SEAS <Text type="secondary">(opcional)</Text>
                </Text>
                <Input size="small" placeholder="Ej: 745437" value={simulationId} onChange={e => setSimulationId(e.target.value)} />
              </div>
            </Space>

            <Button
              type="primary" icon={<ScanOutlined />} block size="large"
              loading={loading} disabled={!canSubmit}
              onClick={handleSubmit}
              style={{ marginTop: 16 }}
            >
              {loading ? 'Procesando…' : `Analizar${filesCount ? ` (${filesCount} archivo${filesCount > 1 ? 's' : ''})` : ''}`}
            </Button>

            {!canSubmit && !loading && (
              <Text type="secondary" style={{ display: 'block', textAlign: 'center', fontSize: 11, marginTop: 6 }}>
                Sube al menos el CC frente o el comprobante
              </Text>
            )}
          </Card>
        </Col>

        {/* ── Right: results ── */}
        <Col xs={24} lg={15}>

          {/* Empty state */}
          {!result && !loading && (
            <div style={{
              display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
              minHeight: 320, color: '#bfbfbf', gap: 12,
            }}>
              <ScanOutlined style={{ fontSize: 56, color: '#d9d9d9' }} />
              <Text type="secondary" style={{ fontSize: 15 }}>Sube los documentos y presiona Analizar</Text>
              <Text type="secondary" style={{ fontSize: 12, textAlign: 'center', maxWidth: 320 }}>
                Se extrae identidad (nombre, cédula, fecha de nacimiento) e ingresos
                (pagaduría, neto mensual, capacidad de endeudamiento) sin intervención manual.
              </Text>
            </div>
          )}

          {/* Loading state */}
          {loading && (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: 320, gap: 12 }}>
              <ScanOutlined style={{ fontSize: 48, color: '#1890ff' }} spin />
              <Text style={{ color: '#1890ff', fontSize: 15 }}>Procesando documentos…</Text>
              <Text type="secondary" style={{ fontSize: 12 }}>Las imágenes pueden tardar hasta 30 segundos</Text>
            </div>
          )}

          {/* Results */}
          {result && (
            <Space direction="vertical" style={{ width: '100%' }} size={14}>

              {/* ── Validation banner ── */}
              <Card
                bordered={false}
                style={{
                  background: bothOk ? '#f6ffed' : anyFail ? '#fff2f0' : '#fffbe6',
                  border: `1px solid ${bothOk ? '#b7eb8f' : anyFail ? '#ffccc7' : '#ffe58f'}`,
                }}
                styles={{ body: { padding: '12px 16px' } }}
              >
                <Row gutter={16} align="middle" wrap={false}>
                  <Col flex="none">
                    {bothOk
                      ? <CheckCircleFilled style={{ fontSize: 20, color: '#52c41a' }} />
                      : anyFail
                        ? <CloseCircleOutlined style={{ fontSize: 20, color: '#ff4d4f' }} />
                        : <QuestionCircleOutlined style={{ fontSize: 20, color: '#faad14' }} />}
                  </Col>
                  <Col flex="auto">
                    <Text strong style={{ fontSize: 13 }}>
                      {bothOk ? 'Documentos validados — identidad consistente'
                        : anyFail ? 'Inconsistencia entre documentos'
                        : 'Validación parcial'}
                    </Text>
                  </Col>
                  <Col flex="none">
                    <Space size={12}>
                      <span style={{ fontSize: 12 }}>
                        CC: <TriBool value={result.cross_validation.cc_match} trueLabel="✓" falseLabel="✗" />
                      </span>
                      <span style={{ fontSize: 12 }}>
                        Nombre: <TriBool value={result.cross_validation.name_match} trueLabel="✓" falseLabel="✗" />
                      </span>
                      <Tag style={{ fontSize: 11 }}>{result.documents_processed} doc{result.documents_processed !== 1 ? 's' : ''}</Tag>
                    </Space>
                  </Col>
                </Row>
              </Card>

              {/* ── Identity + Income side by side on desktop ── */}
              <Row gutter={[14, 14]}>
                <Col xs={24} md={12}>
                  <Card
                    size="small"
                    title={
                      <Space>
                        <IdcardOutlined style={{ color: '#1890ff' }} />
                        <span>Identidad</span>
                        {result.identity && <MethodTag method={result.identity.extraction_method} />}
                      </Space>
                    }
                    styles={{ body: { padding: '12px 16px' } }}
                  >
                    {result.identity ? (
                      <Space direction="vertical" style={{ width: '100%' }} size={8}>
                        <div>
                          <Text type="secondary" style={{ fontSize: 11, display: 'block' }}>Nombre completo</Text>
                          <Text strong>{result.identity.nombre_completo ?? '—'}</Text>
                        </div>
                        <Row gutter={12}>
                          <Col span={12}>
                            <Text type="secondary" style={{ fontSize: 11, display: 'block' }}>Cédula</Text>
                            <Text code style={{ fontSize: 13 }}>{result.identity.numero_documento ?? '—'}</Text>
                          </Col>
                          <Col span={12}>
                            <Text type="secondary" style={{ fontSize: 11, display: 'block' }}>Fecha de nacimiento</Text>
                            <Text>{result.identity.fecha_nacimiento ?? '—'}</Text>
                          </Col>
                        </Row>
                        <div>
                          <Text type="secondary" style={{ fontSize: 11, display: 'block' }}>Lugar de expedición</Text>
                          <Text>{result.identity.lugar_expedicion ?? '—'}</Text>
                        </div>
                      </Space>
                    ) : (
                      <Alert message="No se extrajo identidad" type="warning" showIcon icon={<WarningOutlined />} />
                    )}
                  </Card>
                </Col>

                <Col xs={24} md={12}>
                  <Card
                    size="small"
                    title={
                      <Space>
                        <UserOutlined style={{ color: '#722ed1' }} />
                        <span>Ingresos</span>
                        {result.income && <MethodTag method={result.income.extraction_method} />}
                      </Space>
                    }
                    styles={{ body: { padding: '12px 16px' } }}
                  >
                    {result.income ? (
                      <Space direction="vertical" style={{ width: '100%' }} size={8}>
                        <Row gutter={12}>
                          <Col span={12}>
                            <Text type="secondary" style={{ fontSize: 11, display: 'block' }}>Neto mensual</Text>
                            <Text strong style={{ fontSize: 15, color: '#52c41a' }}>
                              {formatMoney(result.income.neto_mensual)}
                            </Text>
                          </Col>
                          <Col span={12}>
                            <Text type="secondary" style={{ fontSize: 11, display: 'block' }}>Cap. endeudamiento</Text>
                            <Text strong style={{ fontSize: 15, color: '#1890ff' }}>
                              {formatMoney(result.income.capacidad_endeudamiento)}
                            </Text>
                          </Col>
                        </Row>
                        <div>
                          <Text type="secondary" style={{ fontSize: 11, display: 'block' }}>Valor base</Text>
                          <Text>{formatMoney(result.income.valor_base)}</Text>
                        </div>
                        <Row gutter={12}>
                          <Col span={14}>
                            <Text type="secondary" style={{ fontSize: 11, display: 'block' }}>Pagaduría</Text>
                            {result.income.pagaduria_detected
                              ? <Tag color="blue">{result.income.pagaduria_detected}</Tag>
                              : <Text type="secondary">—</Text>}
                          </Col>
                          <Col span={10}>
                            <Text type="secondary" style={{ fontSize: 11, display: 'block' }}>Estado</Text>
                            <TriBool value={result.income.estado_activo} trueLabel="Activo" falseLabel="Inactivo" />
                          </Col>
                        </Row>
                        <Row gutter={12}>
                          <Col span={14}>
                            <Text type="secondary" style={{ fontSize: 11, display: 'block' }}>Titular (voucher)</Text>
                            <Text style={{ fontSize: 12 }}>{result.income.nombre_titular ?? '—'}</Text>
                          </Col>
                          <Col span={10}>
                            <Text type="secondary" style={{ fontSize: 11, display: 'block' }}>Período</Text>
                            <Text style={{ fontSize: 12 }}>{result.income.periodo ?? '—'}</Text>
                          </Col>
                        </Row>
                      </Space>
                    ) : (
                      <Alert message="No se extrajeron ingresos" type="warning" showIcon icon={<WarningOutlined />} />
                    )}
                  </Card>
                </Col>
              </Row>

              {/* ── Warnings ── */}
              {result.errors.length > 0 && (
                <Alert
                  type="warning" showIcon
                  message={`${result.errors.length} advertencia(s) durante el procesamiento`}
                  description={
                    <ul style={{ margin: 0, paddingLeft: 16 }}>
                      {result.errors.map((e, i) => <li key={i} style={{ fontSize: 12 }}>{e}</li>)}
                    </ul>
                  }
                />
              )}

              {/* ── Raw JSON ── */}
              <details>
                <summary style={{ cursor: 'pointer', color: '#8c8c8c', fontSize: 12, userSelect: 'none' }}>
                  Ver JSON completo
                </summary>
                <pre style={{ background: '#f5f5f5', padding: 12, borderRadius: 6, maxHeight: 280, overflow: 'auto', fontSize: 11, marginTop: 6, lineHeight: 1.5 }}>
                  {JSON.stringify(result, null, 2)}
                </pre>
              </details>

            </Space>
          )}
        </Col>
      </Row>
    </div>
  )
}

export default OcrAnalysis
